/**
 * Putting the §6 settings ONTO the document — the only file in the module that
 * touches the DOM's global state.
 *
 * ── WHY THE STYLESHEET IS INJECTED, NOT AUTHORED IN `styles/app.css` ────────
 * Three reasons, in order of weight:
 *   • `app.css` is a Tailwind entry compiled at build time; a `!important`
 *     motion kill-switch written there is invisible to every jsdom test, so the
 *     claim "reduced motion disables every transition" would have no test that
 *     could fail. Injected, it is a string this module owns and a test can
 *     read.
 *   • The rules must exist on any page that renders a game screen, including
 *     the ones mounted straight into a bare `document.body` by a test or by the
 *     landing page's lazy chunk. A module that installs them is a module that
 *     cannot be forgotten.
 *   • It is ~600 bytes and it never changes, so the cost of shipping it in the
 *     shell chunk is a rounding error against the 150 KB budget.
 *
 * ── THE MOTION KILL-SWITCH IS DELIBERATELY BLUNT ────────────────────────────
 * `animation-duration: 0.01ms !important` on `*, ::before, ::after` is the
 * well-known form, and the two details that are easy to get wrong are both
 * here: the duration is 0.01ms rather than 0, because a 0-duration animation
 * never fires `animationend` and any code waiting on that stalls forever; and
 * `animation-iteration-count: 1` is set because an INFINITE animation with a
 * near-zero duration is a busy loop at the compositor, not a stopped animation.
 *
 * It is `!important` and it is universal because §6's requirement is that the
 * game stays fully playable with EVERY motion effect off — including motion in
 * a component this module has never heard of. A curated list would be a list
 * that is one component out of date the day after it is written.
 *
 * The MATCH is not covered by CSS: it is a canvas. It reads the same resolved
 * boolean through `MatchHudSettings.reducedMotion` and `SceneOptions.reduced
 * Motion`, which Phase 2 already threads into the juice layer, the flyer bob,
 * the dissolve, the hit-stop and the deploy lift. `store.ts` is what hands it
 * over; this file only paints the DOM half.
 */
import { A11Y_ATTR, A11Y_SCALE_VAR, a11yDomState, type A11ySettings } from './settings';

export const A11Y_STYLE_ID = 'rga-a11y';

/**
 * The stylesheet, verbatim. Exported so `test/a11y.test.ts` can assert the
 * rules exist rather than assert that a `<style>` tag was appended.
 */
export const A11Y_CSS = `
:root{${A11Y_SCALE_VAR}:1}
html{font-size:calc(100% * var(${A11Y_SCALE_VAR},1))}
[${A11Y_ATTR.motion}="reduced"] *,
[${A11Y_ATTR.motion}="reduced"] *::before,
[${A11Y_ATTR.motion}="reduced"] *::after{
animation-duration:.01ms!important;
animation-iteration-count:1!important;
animation-delay:0s!important;
transition-duration:.01ms!important;
transition-delay:0s!important;
scroll-behavior:auto!important;
}
[${A11Y_ATTR.motion}="reduced"]{scroll-behavior:auto!important}
[${A11Y_ATTR.textScale}]:not([${A11Y_ATTR.textScale}="1"]) body{overflow-wrap:anywhere}
`.replace(/\n/g, '');

/**
 * Ensure the stylesheet is present exactly once. Idempotent, and safe to call
 * before `<head>` exists (it falls back to `documentElement`).
 */
export function installA11yStyles(doc: Document): HTMLStyleElement | null {
  const existing = doc.getElementById(A11Y_STYLE_ID);
  if (existing !== null) return existing as HTMLStyleElement;
  const parent: Element | null = doc.head ?? doc.documentElement ?? null;
  if (parent === null) return null;
  const style = doc.createElement('style');
  style.id = A11Y_STYLE_ID;
  style.textContent = A11Y_CSS;
  parent.appendChild(style);
  return style;
}

/**
 * Write the resolved state onto `<html>`.
 *
 * `data-*` ATTRIBUTES RATHER THAN CLASSES, because these are enumerated states
 * (`motion` is reduced-or-full, `mirrored` is on-or-off) and an attribute
 * cannot end up in the contradictory pair a class list can — there is no way to
 * be `is-reduced-motion is-full-motion` at once. The applier is therefore
 * idempotent by construction: applying twice writes the same four attributes.
 *
 * The root font size is set as a CSS variable, NOT as `style.fontSize`. Setting
 * the inline font size would beat the `@media` and print stylesheets a browser
 * or an extension may legitimately apply; a variable the stylesheet multiplies
 * into `100%` keeps the player's own browser zoom and their browser's minimum
 * font size compounding correctly on top of it, which is what a text-scale
 * setting is supposed to do.
 */
export function applyA11ySettings(
  doc: Document,
  settings: A11ySettings,
  systemPrefersReduced: boolean,
): void {
  installA11yStyles(doc);
  const root = doc.documentElement;
  if (root === null) return;
  const state = a11yDomState(settings, systemPrefersReduced);
  root.setAttribute(A11Y_ATTR.motion, state.motion);
  root.setAttribute(A11Y_ATTR.textScale, state.textScale);
  root.setAttribute(A11Y_ATTR.palette, state.palette);
  root.setAttribute(A11Y_ATTR.mirrored, state.mirrored);
  root.style.setProperty(A11Y_SCALE_VAR, state.textScale);
}

/** Undo `applyA11ySettings`. Tests only — nothing in the app un-applies. */
export function clearA11ySettings(doc: Document): void {
  const root = doc.documentElement;
  if (root === null) return;
  for (const attr of Object.values(A11Y_ATTR)) root.removeAttribute(attr);
  root.style.removeProperty(A11Y_SCALE_VAR);
  doc.getElementById(A11Y_STYLE_ID)?.remove();
}
